/**
 * Project slice.
 *
 * The project list, the active project and tree, and the hydration entry
 * points (setOst, setProjectSharedData) that load a project's data in.
 *
 * Slices share one store: `set` and `get` are typed over the whole AppStore,
 * so an action here can read or write another slice's state where the domains
 * genuinely interact (loading a project resets the tree, converting a triage
 * item calls the tree's addNode).
 */
import type { StateCreator } from 'zustand';
import type { Quality } from '../../components/ui/QualitySlider';
import type { InsightLink, InsightNode, OstState, ProjectModel, TriageItem } from '../../types';
import { normalizeOstState, randomId } from '../../utils/ostUtils';
import { DEFAULT_INSIGHT_CORNER_COLOR } from '../initialData';
import { patchData } from '../persistence';
import { readProjectPref } from '../projectPrefs';
import type { AppStore } from '../storeTypes';

export const createProjectSlice: StateCreator<
    AppStore,
    [],
    [],
    Pick<
        AppStore,
        | 'projects'
        | 'activeProject'
        | 'ostLoadedForProjectId'
        | 'setOstLoadedForProjectId'
        | 'activeOstId'
        | 'treeLoading'
        | 'setTreeLoading'
        | 'setProjects'
        | 'setActiveProject'
        | 'clearProject'
        | 'setOst'
        | 'setProjectSharedData'
        | 'setActiveOst'
    >
> = (set, get) => ({
    projects: [],
    activeProject: null,
    ostLoadedForProjectId: null,
    setOstLoadedForProjectId: (projectId: string | null) => set({ ostLoadedForProjectId: projectId }),
    activeOstId: localStorage.getItem('activeOstId') || null,
    treeLoading: false,
    setTreeLoading: (loading: boolean) => set({ treeLoading: loading }),


    setProjects: (projects: ProjectModel[]) => set({ projects }),

    setActiveProject: (project: ProjectModel | null) => {
        // Persist the active project ID so it can be restored on page refresh.
        if (project) {
            localStorage.setItem('ost_active_project_id', project._id);
            // Load per-project settings when switching to a project so each project
            // has its own independent GitHub integration and AI preferences.
            const pid = project._id;
            const githubRepo = readProjectPref(pid, 'githubRepo') || '';
            const githubProjectId = readProjectPref(pid, 'githubProjectId') || '';
            const aiSuggestionCount = Math.max(1, Math.min(10, parseInt(readProjectPref(pid, 'aiSuggestionCount') || '3', 10)));
            const aiModelQuality = (readProjectPref(pid, 'aiModelQuality') as Quality) || 'low';
            const gridSpacing = parseInt(readProjectPref(pid, 'gridSpacing') || '15', 10) || 15;
            const snapToGrid = (readProjectPref(pid, 'snapToGrid') ?? '1') === '1';
            // Switching to a different project invalidates any previously-loaded shared
            // data (insights/insightLinks/triageItems belong to the OLD project). Only
            // preserve the "loaded" marker when re-setting the SAME project (e.g. an
            // in-place refresh of activeProject fields after a save).
            const previousProject = get().activeProject;
            const sameProject = previousProject?._id === pid;
            const ostLoadedForProjectId = sameProject ? get().ostLoadedForProjectId : null;
            set({
                activeProject: project,
                ostLoadedForProjectId,
                githubRepo,
                githubProjectId,
                aiSuggestionCount,
                aiModelQuality,
                gridSpacing,
                snapToGrid,
                // Preserve current UI collapsed state when switching projects
                insightsPanelMinimized: get().insightsPanelMinimized,
                triagePanelMinimized: get().triagePanelMinimized,
            });
        } else {
            localStorage.removeItem('ost_active_project_id');
            set({
                activeProject: project,
                ostLoadedForProjectId: null,
                // Reset project-scoped settings so stale values don't leak across projects.
                githubRepo: '',
                githubProjectId: '',
                gridSpacing: 15,
                snapToGrid: true,
                insightsPanelMinimized: get().insightsPanelMinimized,
                triagePanelMinimized: get().triagePanelMinimized,
            });
        }
    },

    clearProject: () => set({
        activeProject: null,
        ostLoadedForProjectId: null,
        canvasView: null,
        insightsPanelMinimized: true,
        triagePanelMinimized: true,
        // Clear project-wide shared data when leaving a project.
        insights: [],
        insightLinks: [],
        triageItems: [],
        planPlacements: {},
        planOrder: [],
    }),


    setOst: (ost: OstState) => {
        if (!ost) return;
        const normalized = normalizeOstState(ost);
        const childType: 'story' | 'experiment' = normalized.solutionChildType || 'story';
        // Persist only tree-specific fields; insights/triage are managed at the project level
        // via setProjectSharedData and must not be overwritten when switching trees.
        patchData({ ...normalized, solutionChildType: childType });
        set({
            outcome: normalized.outcome || null,
            opportunities: normalized.opportunities || [],
            solutions: normalized.solutions || [],
            userStories: normalized.userStories || [],
            positions: normalized.positions || {},
            nodePanelPosition: normalized.nodePanelPosition || { x: 20, y: 120 },
            // A different tree's nodes just replaced whatever was on the canvas — drop the
            // remembered view so Canvas.tsx fits the new layout instead of reapplying the
            // previous tree's pan/zoom to it.
            canvasView: null,
            solutionChildType: childType,
            connections: normalized.connections || [],
            insightsPanelPosition: normalized.insightsPanelPosition || { x: 20, y: 260 },
            // Preserve the current UI collapsed state when switching OSTs — do not auto-open
            // the InsightsPanel based on stored OST state.
            insightsPanelMinimized: get().insightsPanelMinimized,
            // Preserve the current UI collapsed state for the triage panel too.
            triagePanelMinimized: get().triagePanelMinimized,
            // NOTE: insights, insightLinks, and triageItems are intentionally NOT set here.
            // They are project-wide and must be loaded/cleared via setProjectSharedData.
        });
    },


    setProjectSharedData: (data: { insights: InsightNode[]; insightLinks: InsightLink[]; triageItems: TriageItem[] }) => {
        // Normalize insight fields to ensure stable defaults.
        const normalizedInsights = (data.insights || []).map((ins) => ({
            id: ins.id || `ins_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`,
            type: 'insight' as const,
            title: ins.title || 'Untitled Insight',
            description: ins.description || '',
            sourceUser: ins.sourceUser || { name: 'Unknown' },
            cornerColor: ins.cornerColor || DEFAULT_INSIGHT_CORNER_COLOR,
            sourceUrl: ins.sourceUrl,
            order: typeof ins.order === 'number' ? ins.order : 0,
            createdAt: ins.createdAt,
        }));
        // Normalize triage items, assigning stable defaults for any missing fields.
        const normalizedTriageItems = (data.triageItems || []).map((item, index) => ({
            id: item.id || randomId(),
            type: 'triage' as const,
            title: item.title || 'Untitled',
            description: item.description || '',
            order: typeof item.order === 'number' ? item.order : index,
            sourceNodeType: item.sourceNodeType,
        }));
        patchData({
            insights: normalizedInsights,
            insightLinks: data.insightLinks || [],
            triageItems: normalizedTriageItems,
        });
        set({
            insights: normalizedInsights,
            insightLinks: data.insightLinks || [],
            triageItems: normalizedTriageItems,
        });
    },


    setActiveOst: (ostId: string | null) => {
        if (ostId) localStorage.setItem('activeOstId', ostId);
        else localStorage.removeItem('activeOstId');
        set({ activeOstId: ostId, suppressInsightsOpenUntil: ostId ? Date.now() + 600 : null });
    },
});
